import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, ScrollView, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';

import styles from './comprehension-true-false.style';
import { useActivityContext } from '../../../hooks/use-activity.context';
import { useScroll } from '../../../hooks/use-scroll';
import trueBeginnerStr from '../../../utils/true-beginner-str';
import { ContinueButton } from '../../continue-button/continue-button';
import { Instruction } from '../../instruction/instruction.component';
import { ThemedView } from '../../themed-view';
import { Title } from '../../title/title.component';
import { TranslatableText } from '../../translatable-text/translatable-text';
import { TrueFalseContainer } from '../../true-false-container/true-false-container';
import { Card } from '../card/card';

export function ComprehensionTrueFalse() {
  const {
    currentIndex,
    currentStep,
    currentActivity,
    isCurrentStepComplete,
    goNext,
    completeCurrentStep,
  } = useActivityContext();

  const scrollRef = useRef<ScrollView>(null);
  const { scrollToEnd, scrollToTop } = useScroll(scrollRef);
  const [optionsAreDisabled, disableOptions] = useState(false);
  const [index, setIndex] = useState(0);
  const [correctFeedback, setCorrectFeedback] = useState(true);
  const [questionOpacity] = useState(new Animated.Value(1));
  const [showNextButton, setShowNextButton] = useState(false);

  const onAnswer = (isCorrect: boolean) => {
    disableOptions(true);
    setTimeout(() => {
      completeCurrentStep();
    }, 500);
    if (isCorrect) {
      setCorrectFeedback(true);
    } else {
      setCorrectFeedback(false);
    }
  };

  const questionFadeOut = useCallback(() => {
    Animated.timing(questionOpacity, {
      toValue: 0,
      duration: 400,
      useNativeDriver: true,
    }).start();
  }, [questionOpacity]);

  const questionFadeIn = useCallback(() => {
    Animated.timing(questionOpacity, {
      toValue: 1,
      duration: 400,
      useNativeDriver: true,
    }).start();
  }, [questionOpacity]);

  const continueWithNextStep = useCallback(() => {
    setShowNextButton(false);
    questionFadeOut();
    setTimeout(() => {
      goNext();
      setIndex((x) => x + 1);
      questionFadeIn();
    }, 800);
  }, [goNext, questionFadeIn, questionFadeOut]);

  useEffect(() => {
    if (isCurrentStepComplete) {
      setShowNextButton(true);
      scrollToEnd();
    }
  }, [isCurrentStepComplete, scrollToEnd]);

  useEffect(() => {
    scrollToTop();
  }, [currentActivity.text, scrollToTop, currentIndex]);

  useEffect(() => {
    disableOptions(false);
  }, [currentStep]);

  return (
    <>
      <ScrollView ref={scrollRef} contentContainerStyle={styles.container}>
        <SafeAreaView edges={['bottom']} style={styles.content}>
          <Instruction
            text={trueBeginnerStr(
              currentActivity,
              'instructions',
              currentActivity.trueBeginner
            )}
          />
          <View style={[styles.view]}>
            <Card containerStyle={styles.card}>
              <TranslatableText
                style={styles.card}
                translation={currentActivity.textTranslation}
                isMarkdown
                isHtml
              >
                {currentActivity.text}
              </TranslatableText>
            </Card>
          </View>
          <ThemedView style={[styles.viewText]}>
            <Animated.View style={[{ opacity: questionOpacity }]}>
              <Title>{currentStep?.question}</Title>
            </Animated.View>
          </ThemedView>
          <TrueFalseContainer
            blocked={optionsAreDisabled}
            onAnswer={onAnswer}
            correctAnswer={!!currentStep?.answer}
            index={index}
          />
        </SafeAreaView>
      </ScrollView>
      {showNextButton && (
        <ContinueButton
          isCorrect={correctFeedback}
          onPress={continueWithNextStep}
        />
      )}
    </>
  );
}
